import type { CompanyRef, UserData } from '@/domain/types/User'
import type { CompanyRole, ModulePermissions } from '@/domain/types/Role'
import { asCompanyRoles, clientTypeForRole, type CompanyRoleEntry } from '@/domain/types/Role'

/**
 * Campos que escribe un usuario existente al unirse a una compañía con un link de
 * invitación. Debe cumplir la regla joinWithInvite de firestore/fase-*.rules:
 * agrega exactamente una compañía al final de companiesRef, conserva todos los
 * companyIds previos y solo cambia la llave de permisos de la compañía nueva.
 *
 * companyRoles: la invitación SIEMPRE fija la entrada de la compañía nueva (el rol global de
 * un usuario existente no cambia, así que sin ella entraría con su rol global), salvo para el
 * superadmin, que es global y nunca la lleva. Devuelve el mapa completo (para el usuario en
 * memoria); quien escribe en Firestore puede limitarse a la llave de la compañía.
 */

/** Entrada de companyRoles que dicta una invitación (igual a inviteCompanyRoleEntry de las reglas). */
export function inviteCompanyRoleEntry(invite: { role: CompanyRole; customRoleId?: string }): CompanyRoleEntry {
  return invite.role === 'regular' && invite.customRoleId
    ? { role: 'regular', customRoleId: invite.customRoleId }
    : { role: invite.role }
}

export function buildInviteJoinUpdate(params: {
  user: Pick<UserData, 'companyIds' | 'companiesRef' | 'permissions'> & Partial<Pick<UserData, 'companyId' | 'role' | 'companyRoles'>>
  newCompanyRef: CompanyRef
  invite: { companyId: string; role: CompanyRole; permissions?: ModulePermissions; inviteId?: string; customRoleId?: string }
}): Record<string, unknown> {
  const { user, newCompanyRef, invite } = params
  const companiesRef = [...(user.companiesRef || []), newCompanyRef]
  const previousIds = user.companyIds || []
  // Solo una cuenta sin NINGUNA compañía (ni companyIds, ni companiesRef, ni companyId) y que no sea
  // superadmin toma el rol de la invitación; a un usuario existente nunca se le cambia el rol global.
  const hasNoCompanies =
    previousIds.length === 0 && (user.companiesRef || []).length === 0 && !user.companyId && user.role !== 'superadmin'
  const companyIds = [...previousIds]
  for (const ref of companiesRef) {
    if (!companyIds.includes(ref.id)) companyIds.push(ref.id)
  }

  return {
    companiesRef,
    companyIds,
    companyId: invite.companyId,
    updatedAt: new Date().toISOString(),
    ...(invite.permissions
      ? { permissions: { ...(user.permissions || {}), [invite.companyId]: invite.permissions } }
      : {}),
    ...(user.role !== 'superadmin'
      ? { companyRoles: { ...asCompanyRoles(user.companyRoles), [invite.companyId]: inviteCompanyRoleEntry(invite) } }
      : {}),
    ...(invite.inviteId ? { joinedViaInviteId: invite.inviteId } : {}),
    ...(hasNoCompanies
      ? { role: invite.role, clientType: clientTypeForRole(invite.role) }
      : {}),
  }
}
